import {
  NumberInput as MantineNumberInput,
  NumberInputProps as MantineNumberInputProps,
  NumberInputHandlers,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { CSSProperties, ReactNode, forwardRef, useRef } from 'react';

import styles from './NumberInput.module.css';

export type NumberInputSize = 'md' | 'lg';

export type NumberInputVariant = 'editable-label';

export interface NumberInputProps extends Omit<
  MantineNumberInputProps,
  'variant' | 'color' | 'styles' | 'classNames' | 'style' | 'size' | 'readOnly'
> {
  /** Input size: 'md' (40px) or 'lg' (48px). Default: 'md' */
  size?: NumberInputSize;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  /** Visual variant */
  variant?: NumberInputVariant;
  /** Show info icon next to label. Pass `true` for default icon or a ReactNode for custom icon */
  labelIcon?: boolean | ReactNode;
  isReadOnly?: boolean;
  /** Show left/right sections even when `isReadOnly` is true (sections are hidden by default in read-only mode) */
  shouldShowSectionWhenReadOnly?: boolean;
}

const toPixels = (value: string | number) =>
  typeof value === 'number' ? `${value}px` : value;

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      size = 'md',
      hideControls = true,
      decimalScale = 0,
      label,
      error,
      disabled,
      isMasked = false,
      isReadOnly,
      placeholder,
      variant,
      labelIcon,
      shouldShowSectionWhenReadOnly,
      rightSection,
      rightSectionWidth,
      leftSectionWidth,
      handlersRef: externalHandlersRef,
      ...props
    },
    ref
  ) => {
    const internalHandlersRef = useRef<NumberInputHandlers>(null);
    const handlersRef = externalHandlersRef || internalHandlersRef;

    const renderLabel = (): ReactNode => {
      if (!label) return undefined;
      if (!labelIcon) return label;

      const icon =
        labelIcon === true ? (
          <IconInfoCircle size={12} stroke={2} />
        ) : (
          labelIcon
        );

      return (
        <span className={styles.labelWithIcon}>
          <span>{label}</span>
          <span className={styles.labelIcon}>{icon}</span>
        </span>
      );
    };

    const renderError = (): ReactNode => {
      if (isReadOnly) return undefined;
      return error || undefined;
    };

    const showControls = !hideControls && !isReadOnly && !rightSection;

    const controlsSection = showControls ? (
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.control}
          onClick={() =>
            (
              handlersRef as React.RefObject<NumberInputHandlers>
            ).current?.increment()
          }
          disabled={disabled}
          tabIndex={-1}
          aria-label="Increment"
        >
          <IconChevronUp size={12} stroke={2} />
        </button>
        <button
          type="button"
          className={styles.control}
          onClick={() =>
            (
              handlersRef as React.RefObject<NumberInputHandlers>
            ).current?.decrement()
          }
          disabled={disabled}
          tabIndex={-1}
          aria-label="Decrement"
        >
          <IconChevronDown size={12} stroke={2} />
        </button>
      </div>
    ) : null;

    const sectionWidthVars: Record<string, string> = {};
    if (rightSectionWidth !== undefined && rightSectionWidth !== null) {
      sectionWidthVars['--right-section-width'] = toPixels(rightSectionWidth);
    }
    if (leftSectionWidth !== undefined && leftSectionWidth !== null) {
      sectionWidthVars['--left-section-width'] = toPixels(leftSectionWidth);
    }

    return (
      <MantineNumberInput
        ref={ref}
        unstyled
        classNames={{
          root: styles.root,
          wrapper: styles.wrapper,
          input: clsx(styles.input, isMasked && 'fs-mask'),
          section: styles.section,
          label: styles.label,
          error: styles.error,
          required: styles.required,
          description: styles.description,
        }}
        wrapperProps={{
          'data-input-size': size,
          ...(shouldShowSectionWhenReadOnly && {
            'data-show-section-readonly': true,
          }),
          style: sectionWidthVars as CSSProperties,
        }}
        data-variant={variant || undefined}
        hideControls
        handlersRef={handlersRef}
        decimalScale={decimalScale}
        rightSection={rightSection || controlsSection}
        rightSectionWidth={rightSectionWidth}
        leftSectionWidth={leftSectionWidth}
        label={renderLabel()}
        error={renderError()}
        disabled={disabled}
        readOnly={isReadOnly}
        placeholder={isReadOnly ? '-' : placeholder}
        {...props}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';
